import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { IDropdownOption } from '../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { ELoadStatus } from '../../../../state/state.interface';
import { IBaseResponse, IGetManyResponse } from '../../../../utilities/http-utilities.service';
import { ESnackbar, SnackbarService } from '../../../../utilities/snackbar.service';
import {
  ICheckIn,
  ICheckInBoardMembersRequest,
  ICheckInRequest,
  ICheckOutRequest,
  IOeeLineCheckInUsers,
} from './work-instruction-check-in.interface';

export interface IWorkInstructionCheckInState {
  itemGuideAssignmentIds: number[];
  checkIns: ICheckIn[];
  checkInsLoading: ELoadStatus;
  checkedOutCheckIns: ICheckIn[];
  checkedOutCheckInsLoading: ELoadStatus;
  boardMembers: IDropdownOption[];
  boardMembersLoading: ELoadStatus;
  oeeLineId: number | null;
  oeeLineUsers: IDropdownOption[];
  oeeLineUsersLoading: ELoadStatus;
  actionLoading: ELoadStatus;
}

@Injectable()
export class WorkInstructionCheckInStore extends ComponentStore<IWorkInstructionCheckInState> {
  public readonly checkIns: Signal<ICheckIn[]> = this.selectSignal(
    (state: IWorkInstructionCheckInState) => state.checkIns,
  );
  public readonly isCheckInsLoading: Signal<boolean> = this.selectSignal(
    (state: IWorkInstructionCheckInState) => state.checkInsLoading === ELoadStatus.loading,
  );
  public readonly checkedOutCheckIns: Signal<ICheckIn[]> = this.selectSignal(
    (state: IWorkInstructionCheckInState) => state.checkedOutCheckIns,
  );
  public readonly isCheckedOutCheckInsLoading: Signal<boolean> = this.selectSignal(
    (state: IWorkInstructionCheckInState) => state.checkedOutCheckInsLoading === ELoadStatus.loading,
  );
  public readonly boardMembers: Signal<IDropdownOption[]> = this.selectSignal(
    (state: IWorkInstructionCheckInState) => state.boardMembers,
  );
  /** Loaded or failed: either way the board member list won't change any more. */
  public readonly isBoardMembersSettled: Signal<boolean> = this.selectSignal((state: IWorkInstructionCheckInState) =>
    WorkInstructionCheckInStore.isSettled(state.boardMembersLoading),
  );
  public readonly oeeLineId: Signal<number | null> = this.selectSignal(
    (state: IWorkInstructionCheckInState) => state.oeeLineId,
  );
  public readonly oeeLineUsers: Signal<IDropdownOption[]> = this.selectSignal(
    (state: IWorkInstructionCheckInState) => state.oeeLineUsers,
  );
  public readonly isOeeLineUsersSettled: Signal<boolean> = this.selectSignal((state: IWorkInstructionCheckInState) =>
    WorkInstructionCheckInStore.isSettled(state.oeeLineUsersLoading),
  );
  public readonly isActionLoading: Signal<boolean> = this.selectSignal(
    (state: IWorkInstructionCheckInState) => state.actionLoading === ELoadStatus.loading,
  );

  /**
   * Loads the ongoing check-ins of the given assignments, on the work instructions and on their steps;
   * later check-in/out actions reload the same set.
   */
  readonly loadCheckIns = this.effect((trigger$: Observable<number[]>) =>
    trigger$.pipe(
      switchMap((itemGuideAssignmentIds: number[]) => {
        this.patchState({ checkInsLoading: ELoadStatus.loading, itemGuideAssignmentIds });

        return this.getOngoingCheckIns(itemGuideAssignmentIds).pipe(
          tapResponse(
            (response: IBaseResponse<ICheckIn[]>) =>
              this.patchState({ checkIns: response.data, checkInsLoading: ELoadStatus.success }),
            () => this.patchState({ checkInsLoading: ELoadStatus.failure }),
          ),
        );
      }),
    ),
  );

  /** Finished check-ins of the assignments loadCheckIns was called with, latest check-out first. */
  readonly loadCheckedOutCheckIns = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() => {
        this.patchState({ checkedOutCheckInsLoading: ELoadStatus.loading });

        return this.getCheckedOutCheckIns(this.get().itemGuideAssignmentIds).pipe(
          tapResponse(
            (response: IBaseResponse<ICheckIn[]>) =>
              this.patchState({ checkedOutCheckIns: response.data, checkedOutCheckInsLoading: ELoadStatus.success }),
            () => this.patchState({ checkedOutCheckInsLoading: ELoadStatus.failure }),
          ),
        );
      }),
    ),
  );

  readonly loadBoardMembers = this.effect((trigger$: Observable<ICheckInBoardMembersRequest>) =>
    trigger$.pipe(
      switchMap(({ boardId, search }) => {
        this.patchState({ boardMembersLoading: ELoadStatus.loading });
        const httpParams: HttpParams = search ? new HttpParams().set('search', search) : new HttpParams();

        return this.getBoardMembers(boardId, httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IDropdownOption>) =>
              this.patchState({ boardMembers: response.data, boardMembersLoading: ELoadStatus.success }),
            () => this.patchState({ boardMembersLoading: ELoadStatus.failure }),
          ),
        );
      }),
    ),
  );

  /** The item's OEE Tracker line and the users checked in to it; a failure leaves the board members only. */
  readonly loadOeeLineUsers = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((itemId: number) => {
        this.patchState({ oeeLineUsersLoading: ELoadStatus.loading });

        return this.getOeeLineUsers(itemId).pipe(
          tapResponse(
            (response: IBaseResponse<IOeeLineCheckInUsers>) =>
              this.patchState({
                oeeLineId: response.data.oeeLineId,
                oeeLineUsers: response.data.users,
                oeeLineUsersLoading: ELoadStatus.success,
              }),
            () => this.patchState({ oeeLineId: null, oeeLineUsers: [], oeeLineUsersLoading: ELoadStatus.failure }),
          ),
        );
      }),
    ),
  );

  readonly checkIn = this.effect((trigger$: Observable<ICheckInRequest>) =>
    trigger$.pipe(
      switchMap((request: ICheckInRequest) => {
        this.patchState({ actionLoading: ELoadStatus.loading });

        return this.addCheckIn(request).pipe(
          tapResponse(
            () => {
              this.patchState({ actionLoading: ELoadStatus.success });
              this.showSuccessMessage(request.successMessage);
              this.reloadCheckIns();
            },
            () => this.patchState({ actionLoading: ELoadStatus.failure }),
          ),
        );
      }),
    ),
  );

  readonly checkOut = this.effect((trigger$: Observable<ICheckOutRequest>) =>
    trigger$.pipe(
      switchMap(({ checkInId, successMessage }: ICheckOutRequest) => {
        this.patchState({ actionLoading: ELoadStatus.loading });

        return this.checkOutCheckIn(checkInId).pipe(
          tapResponse(
            () => {
              this.patchState({ actionLoading: ELoadStatus.success });
              this.showSuccessMessage(successMessage);
              this.reloadCheckIns();
            },
            () => {
              this.patchState({ actionLoading: ELoadStatus.failure });
              this.reloadCheckIns();
            },
          ),
        );
      }),
    ),
  );

  private static isSettled(status: ELoadStatus): boolean {
    return status === ELoadStatus.success || status === ELoadStatus.failure;
  }

  constructor(
    private readonly http: HttpClient,
    private readonly snackbar: SnackbarService,
  ) {
    super({
      actionLoading: ELoadStatus.initial,
      boardMembers: [],
      boardMembersLoading: ELoadStatus.initial,
      checkedOutCheckIns: [],
      checkedOutCheckInsLoading: ELoadStatus.initial,
      checkIns: [],
      checkInsLoading: ELoadStatus.initial,
      itemGuideAssignmentIds: [],
      oeeLineId: null,
      oeeLineUsers: [],
      oeeLineUsersLoading: ELoadStatus.initial,
    });
  }

  private showSuccessMessage(message: string | undefined): void {
    if (message) {
      this.snackbar.open(message, ESnackbar.success);
    }
  }

  /** A check-out moves a check-in to the checked-out list: that list is refreshed too once it has been requested. */
  private reloadCheckIns(): void {
    this.loadCheckIns(this.get().itemGuideAssignmentIds);

    if (this.get().checkedOutCheckInsLoading !== ELoadStatus.initial) {
      this.loadCheckedOutCheckIns();
    }
  }

  private getOngoingCheckIns(itemGuideAssignmentIds: number[]): Observable<IBaseResponse<ICheckIn[]>> {
    const httpParams: HttpParams = new HttpParams().set('itemGuideAssignmentIds', itemGuideAssignmentIds.join(','));

    return this.http.get<IBaseResponse<ICheckIn[]>>('check-ins/ongoing', { params: httpParams });
  }

  private getCheckedOutCheckIns(itemGuideAssignmentIds: number[]): Observable<IBaseResponse<ICheckIn[]>> {
    const httpParams: HttpParams = new HttpParams().set('itemGuideAssignmentIds', itemGuideAssignmentIds.join(','));

    return this.http.get<IBaseResponse<ICheckIn[]>>('check-ins/checked-out', { params: httpParams });
  }

  private getOeeLineUsers(itemId: number): Observable<IBaseResponse<IOeeLineCheckInUsers>> {
    return this.http.get<IBaseResponse<IOeeLineCheckInUsers>>('check-ins/oee-line-users', {
      params: new HttpParams().set('itemId', itemId),
    });
  }

  private getBoardMembers(boardId: number, params: HttpParams): Observable<IGetManyResponse<IDropdownOption>> {
    return this.http.get<IGetManyResponse<IDropdownOption>>(`boards/${boardId}/members`, { params });
  }

  private addCheckIn(request: ICheckInRequest): Observable<IBaseResponse<ICheckIn>> {
    return this.http.post<IBaseResponse<ICheckIn>>('check-ins/check-in', {
      destinationObjectId: request.destination.objectId,
      destinationTypeId: request.destination.typeId,
      sourceObjectId: request.userId,
    });
  }

  private checkOutCheckIn(checkInId: number): Observable<IBaseResponse<ICheckIn>> {
    return this.http.patch<IBaseResponse<ICheckIn>>(`check-ins/${checkInId}/check-out`, {});
  }
}
